import { dialogAwarePopupContainer } from 'module/hooks/use-dialog-hosted-overlays';
import { logger } from 'module/logger';
import { CustomLayoutDrawerContent } from 'module/ui/components/atoms/drawer/custom-layout-drawer';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Color } from 'module/ui/components/styles/colors';
import { WarningAltFilled } from '@carbon/icons-react';
import {
  faChevronDown,
  faChevronLeft,
  faRedo,
} from '@fortawesome/pro-solid-svg-icons';
import { Button, Drawer, DrawerTitle } from '@rocketlane/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Meeting } from 'api/meeting/types';
import { MeetingPageTemplateUiState } from 'api/meeting-page-template/types';
import { PageType } from 'api/page-template/types';
import { StreamAPI } from 'api/stream';
import {
  collectRlCustomSectionTemplateSectionIds,
  groupHtmlFormattingFilterRowsByPromptId,
  streamTokens,
} from 'helpers/stream/tokens-streaming';
import { useSelectedMeetingId } from 'queries/meeting';
import { useGetPageTemplatePreviewQuery } from 'queries/templates';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { LoaderStreamingComponent } from 'components/common/loader-streaming';
import {
  FlexWrapper,
  FlexWrapperWithGutter,
  FlexWrapperWithGutterSize,
} from 'components/common/styles';
import { DocumentEditor } from 'components/organisms/document-editor/document-editor';
import { resolvePageTriggerListFilter } from 'components/organisms/pages-template/shared/page-trigger-list-filter';
import { MeetingList } from 'components/organisms/preview/meeting-list-dropdown';
import { MeetingPageThinkingLogs } from 'components/pages/meeting-page-template/meeting-page-thinking-log-row';

const TitleSection = styled.section`
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-06);
  letter-spacing: var(--letter-spacing-00);
  padding: var(--spacing-05);
`;

const EllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
  color: var(--text-primary);
`;

const ErrorContainer = styled.div`
  margin-bottom: var(--spacing-06);
`;

const AlertWrapper = styled.div<{ color: Color }>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: var(--spacing-05) 0;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    fill: var(--red-50);
  }
`;

const Wrapper = styled.div`
  border-radius: var(--border-radius-default);
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-03);
  max-width: 500px;
  width: 100%;
  height: 32px;
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-placeholder);
`;

const MeetingPagePreviewHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-03);
`;

export const MeetingPagePreviewHeader = ({
  closePreview,
}: {
  closePreview: () => void;
}) => {
  return (
    <MeetingPagePreviewHeaderWrapper>
      <Button
        leftIcon={<FontAwesomeIcon icon={faChevronLeft} size={12} />}
        onClick={() => {
          closePreview();
        }}
      />
    </MeetingPagePreviewHeaderWrapper>
  );
};
const RefreshButton = styled(Button)`
  width: 32px;
  height: 32px;
  border: none;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MeetingTypeDropdownWrapper = styled.div`
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-03);
  height: 32px;
  gap: var(--spacing-03);
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-primary);
  flex-shrink: 0;
  white-space: nowrap;
`;

const PreviewContainer = styled.div<{ isLoading: boolean }>`
  margin: var(--spacing-08) var(--spacing-12);

  .rte-editor {
    display: ${({ isLoading }) => (isLoading ? 'none' : 'block')};
  }
`;

const CenterWrapper = styled.div`
  width: calc(700px + calc(var(--spacing-06) * 2));
  padding: var(--spacing-05) var(--spacing-06) 0;
  margin: 0px auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const ThinkingLogsPanel = styled.div`
  width: 100%;
  margin-bottom: var(--spacing-05);
`;

function MeetingPageLevelPreview({
  isPreviewOpen,
  setIsPreviewOpen,
  templateData,
}: {
  isPreviewOpen: boolean;
  setIsPreviewOpen: (isPreviewOpen: boolean) => void;
  templateData: MeetingPageTemplateUiState;
}) {
  const {
    selectedMeetingId,
    selectedMeetingName,
    setSelectedMeeting,
    clearSelectedMeeting,
  } = useSelectedMeetingId();
  const [isInitializingStream, setIsInitializingStream] = useState(false);
  const [thinkingLogDescriptions, setThinkingLogDescriptions] = useState<
    string[]
  >([]);
  const [thinkingStreamActive, setThinkingStreamActive] = useState(false);
  /** Once true, keep showing the thinking panel for this preview (avoids hiding when stream ends with no log lines yet). */
  const [thinkingPanelShown, setThinkingPanelShown] = useState(false);
  const [thinkingDurationSeconds, setThinkingDurationSeconds] = useState<
    number | null
  >(null);
  const thinkingStartedAtRef = useRef<number | null>(null);
  const thinkingStreamAbortRef = useRef<AbortController | null>(null);
  const intl = useIntl();
  //const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const queryClient = useQueryClient();
  const [editorValue, setEditorValue] = useState<any>(null);

  const {
    data: templatePreview,
    isLoading: isPageLevelPreviewLoading,
    isFetching: isPageLevelPreviewFetching,
    refetch: refetchTemplatePreview,
    error: pagePreviewError,
  } = useGetPageTemplatePreviewQuery({
    meetingId: selectedMeetingId ?? 0,
    pageType: PageType.Meeting,
    enabled: false,
    payload: 'tiptapJson' in templateData ? templateData.tiptapJson : undefined,
  });

  useEffect(() => {
    if (selectedMeetingId) {
      refetchTemplatePreview();
    }
  }, [selectedMeetingId, refetchTemplatePreview]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['page-template-preview'] });
    };
  }, [queryClient]);

  useEffect(() => {
    return () => {
      thinkingStreamAbortRef.current?.abort();
    };
  }, []);

  const templateSectionIdListFromContent = useMemo(() => {
    if (!templatePreview || !('content' in templatePreview)) {
      return [] as string[];
    }

    return collectRlCustomSectionTemplateSectionIds(templatePreview.content);
  }, [templatePreview]);

  const handleClosePreview = useCallback(() => {
    queryClient.cancelQueries({ queryKey: ['page-template-preview'] });
    queryClient.removeQueries({ queryKey: ['page-template-preview'] });
    setEditorValue(null);
    setIsInitializingStream(false);
    setThinkingLogDescriptions([]);
    setThinkingStreamActive(false);
    setThinkingPanelShown(false);
    setThinkingDurationSeconds(null);
    thinkingStartedAtRef.current = null;
    thinkingStreamAbortRef.current?.abort();
    thinkingStreamAbortRef.current = null;
    setIsDropdownOpen(false);
    setIsPreviewOpen(false);
    clearSelectedMeeting();
  }, [queryClient, setIsPreviewOpen, clearSelectedMeeting]);

  const handleMenuClick = (meetingId: number, meeting: Meeting) => {
    setSelectedMeeting(meetingId, meeting.meetingName);
    setTimeout(() => {
      setIsDropdownOpen(false);
    }, 0);
  };

  const meetingsMenu = useMemo(() => {
    const menuItems = [
      {
        label: 'All meetings',
        key: 'all-meetings',
      },
      {
        label: 'My meetings',
        key: 'my-meetings',
      },
    ];

    return menuItems;
  }, []);

  const onStreamContentStart = () => {
    // Keep thinking logs and summary; only mark editor content as streaming.
    setIsInitializingStream(true);
  };

  const onInitialization = async (editor) => {
    const streamingMeta = templatePreview?.streamingMeta;
    const shouldRunThinkingStream = Boolean(
      streamingMeta?.token && streamingMeta?.channelName,
    );

    thinkingStreamAbortRef.current?.abort();
    thinkingStreamAbortRef.current = new AbortController();
    const { signal } = thinkingStreamAbortRef.current;

    if (shouldRunThinkingStream) {
      setThinkingLogDescriptions([]);
      setThinkingStreamActive(true);
      setThinkingPanelShown(true);
      setThinkingDurationSeconds(null);
      thinkingStartedAtRef.current = Date.now();

      try {
        await StreamAPI.streamPageGenerationThinkingLogs({
          token: streamingMeta.token,
          channelName: streamingMeta.channelName,
          signal,
          onThinkingStep: (event) => {
            const description =
              typeof event.data?.description === 'string'
                ? event.data.description.trim()
                : '';

            if (!description) {
              return;
            }

            setThinkingLogDescriptions((prev) => [...prev, description]);
          },
        });
      } catch (e) {
        if ((e as Error).name === 'AbortError') {
          return;
        }

        logger.error('Meeting page thinking stream failed', { error: e });
      } finally {
        setThinkingStreamActive(false);
        if (thinkingStartedAtRef.current != null) {
          const elapsedMs = Date.now() - thinkingStartedAtRef.current;

          setThinkingDurationSeconds(Math.max(1, Math.round(elapsedMs / 1000)));
          thinkingStartedAtRef.current = null;
        }
      }

      if (signal.aborted) {
        return;
      }
    }

    let playbackTokensByPromptId: Record<string, string[]> | undefined;

    if (streamingMeta?.token && streamingMeta?.channelName) {
      try {
        const rows = await StreamAPI.getRunningHtmlFormattingTokens({
          token: streamingMeta.token,
          channelName: streamingMeta.channelName,
        });

        playbackTokensByPromptId =
          groupHtmlFormattingFilterRowsByPromptId(rows);
      } catch (e) {
        logger.error('Failed to fetch HTML formatting tokens for preview', {
          error: e,
        });
        playbackTokensByPromptId = {};
      }
    }

    await streamTokens({
      editor,
      identifierType: 'templateSectionId',
      identifierValues: templateSectionIdListFromContent,
      token: templatePreview?.streamingMeta?.token,
      channelName: templatePreview?.streamingMeta?.channelName ?? '',
      shouldFilterByPromptId: true,
      mockHtmlTokensByPromptId: playbackTokensByPromptId,
      onStart: onStreamContentStart,
      onPostStream: async () => {
        if (!streamingMeta?.token || !streamingMeta?.channelName) {
          return;
        }

        const res = await StreamAPI.getPostProcessingContent({
          token: streamingMeta.token,
          channelName: streamingMeta.channelName,
        });

        setEditorValue(res[0]?.data?.content);
      },
    });
  };

  return (
    <Drawer
      open={isPreviewOpen}
      onClose={handleClosePreview}
      size="calc(100% - 60px)"
      placement="right"
      closable={false}
      showOverlay={false}
    >
      <DrawerTitle className="sr-only">Meeting page preview</DrawerTitle>
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Content>
          <FlexWrapperWithGutter>
            <MeetingPagePreviewHeader closePreview={handleClosePreview} />
            <FlexWrapper
              style={{
                margin: 'auto',
                width: '690px',
                alignItems: 'center',
                gap: 'var(--spacing-03)',
              }}
            >
              <MeetingList
                isDropdownOpen={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                isLoading={false}
                selectedMeetingId={selectedMeetingId}
                handleMenuClick={handleMenuClick}
                filter={resolvePageTriggerListFilter(
                  PageType.Meeting,
                  templateData.filters.pageTriggerCondition,
                )}
                renderTrigger={() => (
                  <Wrapper>
                    <div style={{ flex: 1 }}>
                      {selectedMeetingName ? (
                        <EllipsisText>{selectedMeetingName}</EllipsisText>
                      ) : (
                        <FormattedMessage id="preview.meetings" />
                      )}
                    </div>
                    <div style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faChevronDown} />
                    </div>
                  </Wrapper>
                )}
              />
              <Dropdown
                trigger={['click']}
                menu={{ items: meetingsMenu }}
                getPopupContainer={dialogAwarePopupContainer}
              >
                <MeetingTypeDropdownWrapper>
                  <FlexWrapperWithGutterSize size="5">
                    <div style={{ flex: 1 }}>All meetings</div>
                    <div
                      style={{
                        cursor: 'pointer',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      <FontAwesomeIcon icon={faChevronDown} />
                    </div>
                  </FlexWrapperWithGutterSize>
                </MeetingTypeDropdownWrapper>
              </Dropdown>
              <RefreshButton
                variant="secondary"
                disabled={
                  isPageLevelPreviewLoading || isPageLevelPreviewFetching
                }
                onClick={() => refetchTemplatePreview()}
              >
                <FontAwesomeIcon icon={faRedo} />
              </RefreshButton>
            </FlexWrapper>
            <FlexWrapper>
              <Button value="primary" onClick={handleClosePreview}>
                Close
              </Button>
            </FlexWrapper>
          </FlexWrapperWithGutter>
          <PreviewContainer isLoading={!isInitializingStream}>
            <CenterWrapper>
              {pagePreviewError && (
                <ErrorContainer>
                  <AlertWrapper color="red">
                    <WarningAltFilled size={20} />
                    {pagePreviewError.message}
                  </AlertWrapper>
                </ErrorContainer>
              )}
              {(isPageLevelPreviewLoading && selectedMeetingId != null) ||
              isPageLevelPreviewFetching ? (
                <LoaderStreamingComponent />
              ) : (
                templatePreview && (
                  <>
                    <TitleSection>{templatePreview.title}</TitleSection>
                    {!('content' in templatePreview) ||
                    !templatePreview.content ||
                    (Array.isArray(templatePreview.content) &&
                      templatePreview.content.length === 0) ? (
                      <AlertWrapper color="red">
                        <WarningAltFilled size={20} />
                        No content available for preview
                      </AlertWrapper>
                    ) : (
                      <>
                        {thinkingPanelShown && (
                          <ThinkingLogsPanel>
                            <MeetingPageThinkingLogs
                              descriptions={thinkingLogDescriptions}
                              isStreaming={thinkingStreamActive}
                              completedSummaryLabel={
                                !thinkingStreamActive &&
                                thinkingDurationSeconds != null
                                  ? intl.formatMessage(
                                      {
                                        id: 'preview.meetingPage.thoughtForSeconds',
                                      },
                                      { seconds: thinkingDurationSeconds },
                                    )
                                  : undefined
                              }
                            />
                          </ThinkingLogsPanel>
                        )}
                        {!isInitializingStream &&
                          !thinkingStreamActive &&
                          thinkingLogDescriptions.length === 0 &&
                          !thinkingPanelShown && <LoaderStreamingComponent />}

                        <DocumentEditor
                          onInitialize={onInitialization}
                          value={editorValue ?? templatePreview.content}
                          readonly={true}
                        />
                      </>
                    )}
                  </>
                )
              )}
            </CenterWrapper>
          </PreviewContainer>
        </CustomLayoutDrawerContent.Content>
      </CustomLayoutDrawerContent>
    </Drawer>
  );
}

export { MeetingPageLevelPreview };
